import React, { FC, ReactElement, useEffect, useState } from "react";
import { Draggable } from "@hello-pangea/dnd";
import svgIcons from "../assets/svg/svgIcons";
import { APP_CONFIG } from "../constants/constants";

export type ItemEnableButtonProps = {
  title: string | ReactElement;
  handleEnable: () => void;
  disabled: boolean;
  metaData?: any;
};

type ItemProps = {
  id: string;
  title: string;
  sub_title?: string;
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  show?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  statusTag?: boolean | null; // used to show active/inactive status dot
  metaData?: any;
  NestedEnableButton?: (props: ItemEnableButtonProps) => ReactElement;
  isSortableItem?: boolean;
  draggableIndex?: number;
  uncheckWhenDisabled?: boolean;
};

const DualListboxCheckboxItem: FC<ItemProps> = ({
  id,
  title,
  sub_title = "",
  handleChange,
  show = true,
  defaultChecked = false,
  disabled = false,
  statusTag = null,
  metaData,
  NestedEnableButton,
  isSortableItem = false,
  draggableIndex = 0,
  uncheckWhenDisabled = true, // default behavior is to uncheck the checkbox when it's disabled (setting this to false on dashboard asset filter)
}) => {
  // Create a state for the checked status of the checkbox
  const [isChecked, setIsChecked] = useState(defaultChecked);

  // enabled state for the checkbox
  const [enabled, setEnabled] = useState(!!NestedEnableButton ? false : true);

  // Update the checked status when the checkbox is clicked
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
    handleChange?.(e);
  };

  // Uncheck the checkbox when it's disabled
  useEffect(() => {
    if (uncheckWhenDisabled && disabled) {
      setIsChecked(false);
    }
  }, [disabled, uncheckWhenDisabled]);

  // Sync checked state when defaultChecked changes (from parent)
  useEffect(() => {
    setIsChecked(defaultChecked);
  }, [defaultChecked]);

  const CommonOutputComponent = ({
    isDragging = false,
  }: {
    isDragging?: boolean;
  }) => (
    <label
      htmlFor={`Item_${id}`}
      className={`${show ? "" : "hidden "}flex items-center relative ${disabled /* || !enabled */ ? "cursor-default bg-gray-100 pointer-events-none touch-none" : enabled ? "cursor-pointer hover:bg-accent-blue-paleO66/20 has-[:checked]:bg-accent-blue-paleO66" : ""} gap-4 select-none rounded-lg border ${isDragging ? "border-black/50 bg-accent-blue-pale" : "border-gray-200"} px-4 py-2 transition`}
    >
      {statusTag !== null && (
        <span
          className={`absolute top-2 right-2 inline-block w-[10px] h-[10px] rounded-full ${statusTag ? "bg-accent-green" : "bg-field-error-border"}`}
        ></span>
      )}
      {/* Active/Inactive Text Tag
      <span className={`absolute top-2 right-2 flex rounded-full text-[8px] px-1 py-[1px] uppercase ${statusTag ? 'bg-accent-green/10 text-accent-green' : 'bg-field-error-border/10 text-field-error-border'}`}>
        {t(`admins.filters.${statusTag ? 'active' : 'inactive'}`)}
      </span> */}
      <div className={`flex items-center text-xs`}>
        &#8203;
        <input
          type="checkbox"
          className="size-4 rounded border-gray-300 disabled:bg-gray-400 disabled:border-gray-400"
          id={`Item_${id}`}
          onChange={handleCheckboxChange}
          disabled={disabled || !enabled}
          checked={isChecked}
        />
      </div>

      <span className="flex flex-col justify-center text-sm leading-4 text-gray-900 min-h-9 w-[calc(100%-2rem)] break-words">
        <span>{title}</span>
        {!!sub_title && (
          <span className="text-[11px] text-gray-400 mt-1">{sub_title}</span>
        )}
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <span className="text-[11px] text-gray-400 mt-1">#{id}</span>
        )}

        {/* Nested Enable Button */}
        {NestedEnableButton && !enabled && (
          <NestedEnableButton
            title={title}
            handleEnable={() => {
              // setIsChecked(!isChecked)
              setEnabled(true);
            }}
            disabled={disabled}
            metaData={metaData}
          />
        )}
      </span>
    </label>
  );

  // render non sortable item
  if (!isSortableItem) {
    return (
      <div className="relative" key={`Item_${id}`}>
        <CommonOutputComponent />
      </div>
    );
  }

  // render sortable item
  const draggableId = `draggable-${id}`;

  return (
    <Draggable draggableId={draggableId} index={draggableIndex}>
      {(provided, snapshot) => (
        <div
          className={`relative`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          key={`${draggableId}__${draggableIndex}`}
        >
          <CommonOutputComponent isDragging={snapshot.isDragging} />

          {/* Drag Handle */}
          <div
            className="group absolute top-1/2 right-2 transform -translate-y-1/2 flex items-center justify-center size-8 hover:bg-black/40 rounded-lg cursor-move"
            {...provided.dragHandleProps}
          >
            <img
              src={svgIcons.DragHandleBars}
              alt="Drag Handle"
              className="w-5 h-5 group-hover:contrast-200"
            />
          </div>
        </div>
      )}
    </Draggable>
  );
};
export default DualListboxCheckboxItem;
